import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DiscordIcon } from "@/components/icons";

// Order and keys must match INTAKE_FIELDS in backend/server.py — the server
// rejects anything it doesn't recognise.
const FIELDS = [
  { key: "full_name", label: "Your Name", placeholder: "Ken Kaneki", type: "text" },
  { key: "age", label: "Age", placeholder: "27", type: "text" },
  { key: "height_cm", label: "Height (cm)", placeholder: "180", type: "text" },
  { key: "weight_kg", label: "Weight (kg)", placeholder: "78", type: "text" },
  { key: "experience", label: "Training Experience", placeholder: "2 years, on and off", type: "text" },
  { key: "days_per_week", label: "Days Per Week You Can Train", placeholder: "4", type: "text" },
  { key: "equipment", label: "Gym / Equipment Access", placeholder: "Full commercial gym", type: "text" },
  { key: "goal", label: "Primary Goal", placeholder: "Lean bulk — visible abs, wider back", type: "text" },
  { key: "injuries", label: "Injuries Or Medical Conditions", placeholder: "Left shoulder clicks on overhead press. Nothing diagnosed.", type: "textarea" },
  { key: "diet", label: "Dietary Restrictions", placeholder: "Vegetarian, no dairy", type: "text" },
  { key: "timezone", label: "Your Timezone", placeholder: "Europe/Berlin", type: "text" },
  { key: "preferred_slot", label: "Preferred Weekly Call Slot", placeholder: "Tuesdays after 19:00", type: "text" },
  { key: "notes", label: "Anything Else I Should Know", placeholder: "Shift work — some weeks are chaos.", type: "textarea" },
];

const REQUIRED = ["full_name", "age", "height_cm", "weight_kg", "experience", "days_per_week", "equipment", "goal", "timezone", "preferred_slot"];

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#050505] px-6 py-16" data-testid="intake-page">
    <div className="max-w-2xl mx-auto">{children}</div>
  </div>
);

export default function Intake() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setState("denied");
      return;
    }
    api
      .get(`/intake/${encodeURIComponent(sessionId)}`)
      .then(({ data }) => setState(data.submitted ? "done" : "form"))
      .catch(() => setState("denied"));
  }, [sessionId]);

  const set = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED.filter((k) => !(answers[k] || "").trim());
    if (missing.length) {
      toast.error("Fill in every required field — this is what your plan is built on.");
      return;
    }
    setSending(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f.key, answers[f.key] || ""]));
      await api.post("/intake", { session_id: sessionId, answers: payload, health_consent: consent });
      setState("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try again.");
      setSending(false);
    }
  };

  if (state === "loading") {
    return (
      <Shell>
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto text-[#39FF14] mb-6" size={44} />
          <p className="font-jet text-xs text-[#A1A1AA] tracking-[0.2em]">CHECKING YOUR ORDER…</p>
        </div>
      </Shell>
    );
  }

  if (state === "denied") {
    return (
      <Shell>
        <div className="text-center" data-testid="intake-denied">
          <ShieldAlert className="mx-auto text-[#D90429] mb-6" size={44} />
          <h1 className="font-anton text-3xl uppercase red-text">No coaching order found</h1>
          <p className="text-[#A1A1AA] text-sm mt-4 leading-relaxed max-w-md mx-auto">
            This link isn't tied to a completed coaching order. Use the button in your confirmation
            email, or write to{" "}
            <a href="mailto:aintdeady@gmail.com" className="text-[#39FF14]">aintdeady@gmail.com</a>{" "}
            and we'll sort it out.
          </p>
          <Link to="/" className="btn-brutal inline-flex mt-8">Back to Site</Link>
        </div>
      </Shell>
    );
  }

  if (state === "done") {
    return (
      <Shell>
        <div className="text-center" data-testid="intake-done">
          <CheckCircle2 className="mx-auto text-[#39FF14] mb-6" size={44} />
          <h1 className="font-anton text-4xl uppercase neon-text">Intake locked in.</h1>
          <p className="text-[#A1A1AA] text-sm mt-4 leading-relaxed max-w-md mx-auto">
            I've got everything I need to build your plan. Next step: join the Discord and post your
            VIP code — your private coaching channel opens within 24 hours. That's where your
            45-minute kickoff call happens.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center mt-10">
            <a
              href="https://discord.gg/CPyetCc9HX"
              target="_blank"
              rel="noreferrer"
              className="btn-brutal btn-brutal-green"
            >
              <DiscordIcon size={18} /> Join VIP Discord
            </a>
            <Link to="/" className="btn-brutal">Back to Site</Link>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="section-label">// COACHING INTAKE //</p>
      <h1 className="font-anton text-4xl sm:text-5xl uppercase text-[#F5F5F5] leading-none mt-3">
        Tell me what I'm <span className="neon-text">working with</span>
      </h1>
      <p className="text-[#A1A1AA] text-sm mt-4 leading-relaxed">
        Five minutes now saves a week of guessing. Your training plan, your nutrition targets and
        your first call are all built on these answers. Be honest — especially about the injuries.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label htmlFor={f.key} className="font-jet text-[10px] tracking-[0.25em] text-[#39FF14] uppercase block mb-2">
              {f.label}
              {REQUIRED.includes(f.key) && <span className="text-[#D90429]"> *</span>}
            </label>
            {f.key === "injuries" && (
              <p className="font-jet text-[10px] text-[#52525B] tracking-[0.15em] uppercase mb-2 leading-relaxed">
                ADY is not a doctor. This is not medical advice. If something needs a physician, see
                one first.
              </p>
            )}
            {f.type === "textarea" ? (
              <textarea
                id={f.key}
                data-testid={`intake-${f.key}`}
                className="input-ady min-h-[110px] resize-y"
                maxLength={2000}
                placeholder={f.placeholder}
                value={answers[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
              />
            ) : (
              <input
                id={f.key}
                data-testid={`intake-${f.key}`}
                className="input-ady"
                maxLength={2000}
                placeholder={f.placeholder}
                value={answers[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </div>
        ))}

        <label className="flex items-start gap-3 cursor-pointer select-none border border-dashed border-[#39FF14] p-5">
          <input
            data-testid="intake-consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#39FF14] cursor-pointer"
          />
          <span className="font-jet text-[10px] text-[#A1A1AA] leading-relaxed">
            Ich willige ausdrücklich ein, dass meine Gesundheitsdaten (Verletzungen,
            Vorerkrankungen, Körpermaße) zur Durchführung des Coachings verarbeitet werden
            (Art. 9 Abs. 2 lit. a DSGVO). Ich kann diese Einwilligung jederzeit widerrufen. Die
            Daten werden nach Ablauf der Aufbewahrungsfrist automatisch gelöscht. Es gilt die{" "}
            <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#39FF14]">
              Datenschutzerklärung
            </a>
            .
          </span>
        </label>

        <button
          type="submit"
          data-testid="intake-submit"
          className="btn-brutal w-full"
          disabled={!consent || sending}
          title={consent ? "" : "Consent is required before submitting"}
        >
          {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          {sending ? "Sending…" : "Send It"}
        </button>
        <p className="font-jet text-[10px] text-[#52525B] text-center">
          Submitted once — double-check before you send • Stored on ADY's own server, never shared
        </p>
      </form>
    </Shell>
  );
}
